import { useMemo, useState } from 'react';
import type { HistoryResponse, LeagueBundle, LineupPlayer, PlayersFile } from '../../shared/types';
import {
  Avatar,
  Empty,
  ErrorState,
  Legend,
  Loading,
  PageHead,
  Section,
  Stat,
} from '../components/common';
import { useLineups } from '../lib/api';
import { fmtPct } from '../lib/format';
import { analyseAll, coachingTable, fitMargins, luckTable, pairGames } from '../lib/projections';
import { managerDirectory } from '../lib/rivalries';

/**
 * Signed numbers, coloured only when the sign means something.
 *
 * Zero is rendered as a neutral em-dash on purpose: a GREEN dash reads as a
 * gain at a glance, which is exactly the mistake this codebase has shipped
 * before on the assets page.
 */
function Signed({
  value,
  digits = 2,
  goodWhen = 'positive',
  suffix = '',
}: {
  value: number;
  digits?: number;
  goodWhen?: 'positive' | 'negative';
  suffix?: string;
}) {
  if (Math.abs(value) < 0.005) return <span className="faint tabnum">—</span>;
  const good = goodWhen === 'positive' ? value > 0 : value < 0;
  return (
    <span className={`tabnum ${good ? 'good' : 'bad'}`}>
      {value > 0 ? '+' : '−'}
      {Math.abs(value).toFixed(digits)}
      {suffix}
    </span>
  );
}

function PlayerBit({ p, players }: { p: LineupPlayer; players: PlayersFile | null }) {
  const name = players?.players[p[0]]?.[0] ?? p[0];
  return (
    <span className="nowrap">
      <span className={`pos pos-${p[1] ?? 'FLEX'}`}>{p[1] ?? '?'}</span> {name}
    </span>
  );
}

export default function Luck({
  bundle,
  history,
  players,
}: {
  bundle: LeagueBundle;
  history: HistoryResponse | null;
  players: PlayersFile | null;
}) {
  const { data, error, loading, reload } = useLineups();
  const dir = useMemo(() => managerDirectory(bundle, history), [bundle, history]);
  const [season, setSeason] = useState<string>('all');

  const all = useMemo(() => analyseAll(data), [data]);

  const seasons = useMemo(
    () => [...new Set(all.map((w) => w.season))].sort((a, b) => Number(b) - Number(a)),
    [all],
  );

  const weeks = useMemo(
    () => (season === 'all' ? all : all.filter((w) => w.season === season)),
    [all, season],
  );
  const games = useMemo(() => pairGames(weeks), [weeks]);

  // The margin model is always fitted on EVERY game on record, never on the
  // filtered slice. A single season is 81 games at best and the fit would swing
  // wildly year to year, which would make one manager's luck score depend on
  // which tab happened to be open.
  const fit = useMemo(() => fitMargins(pairGames(all)), [all]);

  const coaching = useMemo(() => coachingTable(weeks), [weeks]);
  const luck = useMemo(() => luckTable(games, weeks, fit), [games, weeks, fit]);

  const name = (id: string) => dir.get(id)?.name ?? 'Unknown';
  const avatar = (id: string) => dir.get(id)?.avatar ?? null;

  const notable = useMemo(() => {
    const withMiss = weeks.filter((w) => w.biggestMiss !== null);
    return {
      worst: [...withMiss].sort((a, b) => b.biggestMiss!.swing - a.biggestMiss!.swing).slice(0, 5),
      bestCalls: [...weeks].sort((a, b) => a.coachingCost - b.coachingCost).slice(0, 5),
      unluckiest: [...weeks].sort((a, b) => b.unforeseeable - a.unforeseeable).slice(0, 5),
    };
  }, [weeks]);

  if (loading) return <Loading label="Loading every lineup ever set" />;
  if (error || !data) {
    return <ErrorState message={error ?? 'No lineup data returned'} onRetry={reload} />;
  }
  if (all.length === 0) {
    return (
      <>
        <PageHead title="Luck & Coaching" />
        <div className="card">
          <Empty>No completed weeks yet.</Empty>
        </div>
      </>
    );
  }

  const favourites = games.filter((g) => g.favouriteId !== null);
  const favWon = favourites.filter((g) => g.favouriteId === g.winnerId).length;

  return (
    <>
      <PageHead
        title="Luck & Coaching"
        meta={[
          `${games.length} games`,
          seasons.length === 1 ? seasons[0] : `${seasons[seasons.length - 1]}-${seasons[0]}`,
          `Projections: ${data.projectionSource}`,
        ]}
      />

      {seasons.length > 1 && (
        <div className="toolbar">
          <button className={`chip ${season === 'all' ? 'on' : ''}`} onClick={() => setSeason('all')}>
            All seasons
          </button>
          {seasons.map((s) => (
            <button
              key={s}
              className={`chip ${season === s ? 'on' : ''}`}
              onClick={() => setSeason(s)}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <Section title="Projection accuracy" hint={`Fitted on all ${pairGames(all).length} games`}>
        <div className="grid grid-4">
          <Stat
            label="Favourite wins"
            value={favourites.length === 0 ? '—' : fmtPct(favWon / favourites.length, 1)}
            note={`${favWon} of ${favourites.length} games`}
          />
          <Stat
            label="Margin correlation"
            value={fit.fitted ? fit.r.toFixed(3) : '—'}
            note="projected margin vs actual"
          />
          <Stat
            label="Unexplained swing"
            value={fit.fitted ? `±${fit.sigma.toFixed(1)}` : '—'}
            note="points a projection cannot call"
          />
          <Stat
            label="Margin multiplier"
            value={fit.fitted ? `${fit.slope.toFixed(2)}×` : '—'}
            note="real margins run wider than projected"
          />
        </div>
        <p className="faint" style={{ fontSize: 12, marginTop: 10 }}>
          {data.projectionSource}, not this league's scoring. Read as player rankings, not predicted
          totals.
        </p>
      </Section>

      <Section title="Luck">
        <div className="card table-scroll">
          <table className="picks-table">
            <thead>
              <tr>
                <th>Manager</th>
                <th className="num">Record</th>
                <th className="num">Expected</th>
                <th className="num">Above exp.</th>
                <th className="num">All-play</th>
                <th className="num">Schedule luck</th>
                <th className="num">Projection edge</th>
              </tr>
            </thead>
            <tbody>
              {luck.map((r) => (
                <tr key={r.userId}>
                  <td>
                    <div className="team-cell">
                      <Avatar id={avatar(r.userId)} alt={name(r.userId)} />
                      <div className="team-names">
                        <div className="team-title">{name(r.userId)}</div>
                        <div className="team-handle">favoured in {r.favouredIn} of {r.games}</div>
                      </div>
                    </div>
                  </td>
                  <td className="num tabnum">
                    {r.wins}-{r.losses}
                    {r.ties > 0 ? `-${r.ties}` : ''}
                  </td>
                  <td className="num tabnum">{r.expectedWins.toFixed(2)}</td>
                  <td className="num">
                    <Signed value={r.winsAboveExpectation} />
                  </td>
                  <td className="num tabnum faint">
                    {fmtPct(r.allPlayGames === 0 ? 0 : r.allPlayWins / r.allPlayGames, 0)}
                  </td>
                  <td className="num">
                    <Signed value={r.scheduleLuck} />
                  </td>
                  <td className="num">
                    <Signed value={r.projectionEdgePerWeek} digits={1} suffix="/wk" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Legend
          items={[
            { term: 'Expected', def: 'wins the fitted margin model gives you' },
            { term: 'Above exp.', def: 'wins minus expected' },
            { term: 'All-play', def: 'win rate against the whole league each week' },
            { term: 'Schedule luck', def: 'record versus all-play rate' },
            { term: 'Projection edge', def: 'lineup versus its own projection, centred on the league week' },
          ]}
        />
      </Section>

      <Section title="Coaching">
        <div className="card table-scroll">
          <table className="picks-table">
            <thead>
              <tr>
                <th>Manager</th>
                <th className="num">Foreseeable /wk</th>
                <th className="num">Unforeseeable /wk</th>
                <th className="num">Total regret /wk</th>
                <th className="num">Clear misplays</th>
                <th className="num">Beat the chalk</th>
              </tr>
            </thead>
            <tbody>
              {coaching.map((r) => (
                <tr key={r.userId}>
                  <td>
                    <div className="team-cell">
                      <Avatar id={avatar(r.userId)} alt={name(r.userId)} />
                      <div className="team-names">
                        <div className="team-title">{name(r.userId)}</div>
                        <div className="team-handle">{r.weeks} weeks</div>
                      </div>
                    </div>
                  </td>
                  <td className="num">
                    <Signed value={r.coachingCostPerWeek} digits={2} goodWhen="negative" />
                  </td>
                  <td className="num tabnum faint">{r.unforeseeablePerWeek.toFixed(2)}</td>
                  <td className="num tabnum">{r.regretPerWeek.toFixed(2)}</td>
                  <td className="num tabnum">{r.clearMistakes}</td>
                  <td className="num tabnum">
                    {r.beatChalk}
                    <span className="faint"> / {r.weeks}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Legend
          items={[
            { term: 'Foreseeable', def: 'what following the projections would have gained; negative beat them' },
            { term: 'Unforeseeable', def: 'bench points only hindsight could have caught' },
            { term: 'Total regret', def: 'the two summed' },
            { term: 'Clear misplays', def: 'benched player projected higher and scored higher' },
            { term: 'Beat the chalk', def: 'weeks your lineup outscored the projections’ own' },
          ]}
        />
      </Section>

      <Section title="Notable weeks">
        <div className="grid grid-3 grid-start">
          <article className="card card-pad">
            <div className="record-head">
              <h3>Costliest misplays</h3>
              <span className="hint">Benched player was projected higher and scored higher</span>
            </div>
            <ol className="record-rest">
              {notable.worst.map((w, i) => (
                <li key={`${w.userId}-${w.season}-${w.week}`}>
                  <span className="record-rank">{i + 1}</span>
                  <span className="record-rest-value tabnum bad">
                    −{w.biggestMiss!.swing.toFixed(1)}
                  </span>
                  <span className="record-rest-name" style={{ minWidth: 0 }}>
                    {name(w.userId)}
                    <div className="faint" style={{ fontSize: 11 }}>
                      sat <PlayerBit p={w.biggestMiss!.benched} players={players} /> for{' '}
                      <PlayerBit p={w.biggestMiss!.started} players={players} />
                    </div>
                  </span>
                  <span className="record-rest-when">
                    {w.season} · wk {w.week}
                  </span>
                </li>
              ))}
            </ol>
          </article>

          <article className="card card-pad">
            <div className="record-head">
              <h3>Best calls</h3>
              <span className="hint">Most points gained by ignoring the projections</span>
            </div>
            <ol className="record-rest">
              {notable.bestCalls.map((w, i) => (
                <li key={`${w.userId}-${w.season}-${w.week}`}>
                  <span className="record-rank">{i + 1}</span>
                  <span className="record-rest-value tabnum good">
                    +{Math.abs(w.coachingCost).toFixed(1)}
                  </span>
                  <span className="record-rest-name">{name(w.userId)}</span>
                  <span className="record-rest-when">
                    {w.season} · wk {w.week}
                  </span>
                </li>
              ))}
            </ol>
          </article>

          <article className="card card-pad">
            <div className="record-head">
              <h3>Nothing you could do</h3>
              <span className="hint">Most bench points the projections pointed away from</span>
            </div>
            <ol className="record-rest">
              {notable.unluckiest.map((w, i) => (
                <li key={`${w.userId}-${w.season}-${w.week}`}>
                  <span className="record-rank">{i + 1}</span>
                  <span className="record-rest-value tabnum">{w.unforeseeable.toFixed(1)}</span>
                  <span className="record-rest-name">{name(w.userId)}</span>
                  <span className="record-rest-when">
                    {w.season} · wk {w.week}
                  </span>
                </li>
              ))}
            </ol>
          </article>
        </div>
      </Section>
    </>
  );
}
